import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  FormControl,
  FormControlLabel,
  Collapse,
  InputAdornment,
} from "@mui/material";
import { Zap, Plus, X, CalendarDays, Clock } from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import FlexBox from "components/common/FlexBox";
import TextField from "components/common/TextField";
import Checkbox from "components/common/Checkbox";
import AdminFamilySelect from "components/creation/AdminFamilySelect";
import { useAdminCreateDelegationTask } from "services/delegation";
import { AdminCreateDelegationTaskRequest } from "protogen/delegation_service_pb";
import { LIGHTNING_TASK_TYPE_PHONE_CALLS } from "types/delegation";

type DetailRow = { id: string; name: string; value: string };

const newRow = (): DetailRow => ({
  id: Math.random().toString(),
  name: "",
  value: "",
});

export default ({
  onCreated,
  onCancel,
}: {
  onCreated?: () => void;
  onCancel?: () => void;
}) => {
  const [familyRef, setFamilyRef] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [timeEstimate, setTimeEstimate] = useState<number | undefined>(
    undefined,
  );
  const [details, setDetails] = useState<DetailRow[]>([]);
  const [hasSchedule, setHasSchedule] = useState(false);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { request, loading } = useAdminCreateDelegationTask();

  const updateDetail = (id: string, patch: Partial<DetailRow>) =>
    setDetails((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );

  const handleSubmit = async () => {
    setError(null);
    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    let actionDateSec = 0;
    if (hasSchedule) {
      if (!scheduledDate || !scheduledTime) {
        setError("Both date and time are required when scheduling");
        return;
      }
      const combined = new Date(scheduledDate);
      combined.setHours(scheduledTime.getHours());
      combined.setMinutes(scheduledTime.getMinutes());
      combined.setSeconds(0, 0);
      actionDateSec = Math.floor(combined.getTime() / 1000);
    }

    const cleanedDetails = details
      .filter((row) => row.name.trim())
      .map((row) => ({ name: row.name.trim(), value: row.value }));

    // Protobuf int64 (actionDateSec) expects bigint in TS but BigInt is not JSON serializable;
    // number works at runtime. Type assertion needed.
    const result = await request({
      title: title.trim(),
      description,
      detailsJson: cleanedDetails.length ? JSON.stringify(cleanedDetails) : "",
      serviceType: LIGHTNING_TASK_TYPE_PHONE_CALLS,
      actionDateSec,
      timeEstimateMinutes: timeEstimate || 0,
      familyRef: familyRef || "",
    } as unknown as AdminCreateDelegationTaskRequest);

    if (result) {
      onCreated?.();
    } else {
      setError("Failed to create lightning task");
    }
  };

  return (
    <FlexBox direction="column" gap="20px" sx={{ paddingTop: "8px" }}>
      <FlexBox direction="column" gap="8px">
        <AdminFamilySelect
          title="Family (optional)"
          selectedFamily={familyRef}
          onChange={(ref) => setFamilyRef(ref)}
        />
        {!familyRef && (
          <>
            <Typography variant="bodySmall" sx={{ color: "#6B6E7B" }}>
              No family selected — communication will be between you and the
              delegated advisor.
            </Typography>
            <Typography variant="bodySmall" sx={{ color: "#B26A00" }}>
              ⚠️ Secrets can't be shared unless a family is selected.
            </Typography>
          </>
        )}
      </FlexBox>

      <TextField
        label="Title"
        fullWidth
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <TextField
        label="Instructions"
        multiline
        minRows={3}
        fullWidth
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <TextField
        type="number"
        label="Estimated time (min)"
        value={timeEstimate ?? ""}
        onChange={(e) => {
          const val = e.target.value;
          setTimeEstimate(val === "" ? undefined : parseInt(val, 10));
        }}
        sx={{
          width: { xs: "100%", sm: "180px" },
          "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button":
            {
              WebkitAppearance: "none",
              margin: 0,
            },
          'input[type="number"]': { MozAppearance: "textfield" },
        }}
      />

      <FlexBox direction="column" gap="8px">
        <FlexBox alignItems="center" justifyContent="space-between">
          <Typography variant="bodyHeavy">Details</Typography>
          <Button
            variant="text"
            startIcon={<Plus size={16} />}
            onClick={() => setDetails((prev) => [...prev, newRow()])}
            sx={{ height: "unset" }}
          >
            Add
          </Button>
        </FlexBox>
        <Typography variant="bodySmall" sx={{ color: "#6B6E7B" }}>
          Structured facts the specialist needs to complete the task (name and
          value).
        </Typography>
        {details.length === 0 && (
          <Typography
            variant="bodySmall"
            sx={{ color: "#B3AFB6", fontStyle: "italic" }}
          >
            No details added
          </Typography>
        )}
        {details.map((row) => (
          <FlexBox key={row.id} alignItems="center" gap="8px">
            <TextField
              placeholder="Name"
              value={row.name}
              onChange={(e) => updateDetail(row.id, { name: e.target.value })}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <TextField
              placeholder="Value"
              value={row.value}
              onChange={(e) => updateDetail(row.id, { value: e.target.value })}
              sx={{ flex: 2, minWidth: 0 }}
            />
            <X
              size={18}
              color="#6B6E7B"
              style={{ cursor: "pointer", flexShrink: 0 }}
              onClick={() =>
                setDetails((prev) => prev.filter((r) => r.id !== row.id))
              }
            />
          </FlexBox>
        ))}
      </FlexBox>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "12px 16px",
          border: "1px solid #D4D4D4",
          borderRadius: "8px",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={hasSchedule}
              onChange={(e) => {
                setHasSchedule(e.target.checked);
                if (!e.target.checked) {
                  setScheduledDate(null);
                  setScheduledTime(null);
                }
              }}
              size="small"
            />
          }
          label={
            <Typography variant="bodySmall">Schedule for later</Typography>
          }
        />
        <Collapse in={hasSchedule}>
          <Typography
            variant="bodySmall"
            sx={{ color: "#6B6E7B", marginBottom: "8px" }}
          >
            This task will not be made available for a specialist to pick up
            until the scheduled time.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              ".MuiInputBase-root": { minHeight: "48px" },
            }}
          >
            <FormControl sx={{ flex: 1, minWidth: "140px" }}>
              <DatePicker
                value={scheduledDate}
                onChange={(newValue) => setScheduledDate(newValue)}
                format="MM/dd/yyyy"
                open={datePickerOpen}
                onClose={() => setDatePickerOpen(false)}
                slotProps={{
                  textField: {
                    size: "small",
                    onClick: () => setDatePickerOpen(true),
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setDatePickerOpen(true)}
                            edge="end"
                          >
                            <CalendarDays size={20} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  },
                }}
              />
            </FormControl>
            <DesktopTimePicker
              value={scheduledTime}
              sx={{ flex: 1, minWidth: "120px" }}
              onChange={(newValue) => setScheduledTime(newValue)}
              open={timePickerOpen}
              onClose={() => setTimePickerOpen(false)}
              slotProps={{
                textField: {
                  size: "small",
                  onClick: () => setTimePickerOpen(!timePickerOpen),
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setTimePickerOpen(!timePickerOpen)}
                          edge="end"
                        >
                          <Clock size={20} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                },
              }}
            />
          </Box>
        </Collapse>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <FlexBox justifyContent="flex-end" gap="12px">
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Zap size={16} fill="currentColor" />
            )
          }
        >
          Create lightning task
        </Button>
      </FlexBox>
    </FlexBox>
  );
};
